// ── program-detail-helpers: pure id-guard + label helpers (no JSX) ─────────
// Wave 2 (Programs). The program id is a STRING (bigint-safe) — passed VERBATIM
// to the typed client, NEVER Number()'d. A non-empty len<=200 string is the only
// client guard a real program id can take (mirrors the server's id parsing); a
// bad/absent id can't be a real record, so the detail page sends it back to the
// list rather than firing a doomed read.

export function isValidProgramId(value: string | null | undefined): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 200;
}

/** Friendly program display name, never a fabricated value. */
export function programDisplayName(name: string | null | undefined): string {
  const trimmed = (name ?? "").trim();
  return trimmed.length > 0 ? trimmed : "Unnamed program";
}

/**
 * Neutral lifecycle label for the detail header badge. Reads may carry a WIDER
 * status set than create/edit accept (no DB CHECK on the status column —
 * blueprint §1 trap 5): create only writes draft|active|archived, but a read can
 * surface e.g. "published"/"completed". Any unknown status is shown verbatim
 * (humanized by the caller) rather than dropped — never a fabricated value.
 */
export function programStatusLabel(status: string | null | undefined): string {
  if (!status) return "Draft";
  switch (status) {
    case "draft":
      return "Draft";
    case "active":
      return "Active";
    case "archived":
      return "Archived";
    default:
      return status;
  }
}

/** Badge tone for a program status (active = success, draft = default, else warning). */
export function programStatusTone(status: string | null | undefined): "success" | "warning" | "default" {
  if (status === "active") return "success";
  if (status === "draft" || status == null) return "default";
  return "warning";
}
